import { useRive, useStateMachineInput } from '@rive-app/react-canvas';
import { type CustomSelectOptionProps, CustomSelect, CustomSelectOption } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type ChangeEvent, memo, useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { NestleFormWrapperContext } from 'reactApp/sections/Nestle/FormWrapper/FormWrapper.context';
import { animations, animationsMobile, backgroundImages, backgroundImagesMobile } from 'reactApp/sections/Nestle/PetsCard/Breed/Breed.data';
import { breeds } from 'reactApp/sections/Nestle/PetsCard/PetsCard.data';

import commonStyles from '../PetsCard.css';
import styles from './Breed.css';

const RIVE_STATE_MACHINE_NAME = 'Stars State Machine ';
const RIVE_STATE_MACHINE_NAME_MOBILE = 'State Machine 1';
const RIVE_TRIGGER_NAME = 'Trigger 1';

export const Breed = memo(() => {
    const [animationSrc, setAnimationSrc] = useState<string>('');
    const [animationSrcMobile, setAnimationSrcMobile] = useState<string>('');

    const { formData, formErrors, setFormData, setFormErrors } = useContext(NestleFormWrapperContext);
    const { animal, cardColor } = formData;

    const isMobile = useSelector(QuotaLandingSelectors.isMobile);

    const backgroundImage = backgroundImages[cardColor];
    const backgroundImageMobile = backgroundImagesMobile[cardColor];

    const { rive, RiveComponent } = useRive({
        src: animationSrc,
        stateMachines: RIVE_STATE_MACHINE_NAME,
        autoplay: true,
    });

    const { rive: riveMobile, RiveComponent: RiveComponentMobile } = useRive({
        src: animationSrcMobile,
        stateMachines: RIVE_STATE_MACHINE_NAME_MOBILE,
        autoplay: true,
    });

    const triggerAnimation = useStateMachineInput(rive, RIVE_STATE_MACHINE_NAME, RIVE_TRIGGER_NAME);
    const triggerAnimationMobile = useStateMachineInput(riveMobile, RIVE_STATE_MACHINE_NAME_MOBILE, RIVE_TRIGGER_NAME);

    const renderSelectOptions = ({ option, ...restProps }: CustomSelectOptionProps) => {
        return (
            <CustomSelectOption {...restProps} selected={false} className={commonStyles.selectOption}>
                {option.label}
            </CustomSelectOption>
        );
    };

    const handleChange = useCallback(
        (event: ChangeEvent<HTMLSelectElement>) => {
            setFormData((prev) => ({ ...prev, breed: event.target.value }));

            if (formErrors.breed) {
                setFormErrors((prev) => ({ ...prev, breed: false }));
            }

            if (isMobile) {
                triggerAnimationMobile?.fire();
            } else {
                triggerAnimation?.fire();
            }
        },
        [setFormData, triggerAnimation, triggerAnimationMobile, isMobile, formErrors.breed, setFormErrors]
    );

    useEffect(() => {
        setAnimationSrc(animations[cardColor]);
        setAnimationSrcMobile(animationsMobile[cardColor]);
    }, [cardColor, setAnimationSrc, setAnimationSrcMobile]);

    return (
        <div className={classNames(commonStyles.field, commonStyles.breed)}>
            <span className={commonStyles.label}>порода</span>
            <CustomSelect
                options={animal ? breeds[animal] : []}
                searchable
                placeholder="напишите породу"
                className={classNames(commonStyles.select, {
                    [commonStyles.inputError]: formErrors.breed,
                })}
                renderOption={renderSelectOptions}
                popupDirection="bottom"
                forceDropdownPortal={false}
                onChange={handleChange}
                disabled={!animal}
            />
            <div className={styles.animation}>
                <img src={backgroundImage} alt="breed-background" className={styles.image} />
                <img src={backgroundImageMobile} alt="breed-background" className={styles.imageMobile} />
                <div className={styles.animationDesktop}>
                    <RiveComponent key={animationSrc} />
                </div>
                <div className={styles.animationMobile}>
                    <RiveComponentMobile key={animationSrcMobile} />
                </div>
            </div>
        </div>
    );
});

Breed.displayName = 'Breed';
